/**
 * 视角模板编辑器（通用组件）
 *
 * 三类资产（角色 / 物品 / 场景）共用此组件，差异通过 props 注入：
 *  - title / description：tab 顶部说明
 *  - load / save：分别调三套 admin 接口
 *  - placeholderName / placeholderSuffix：示例文案
 *
 * 视角列表彼此独立：在三个 tab 之间切换不会相互影响数据。
 */

import { useEffect, useState } from 'react';
import { Loader2, Plus, Save, Trash2 } from 'lucide-react';
import toast from '../../Toast';
import type { AngleTemplate } from '../../../apps/director/services/directorAdminApi';

interface Props {
  title: string;
  description: string;
  placeholderName: string;
  placeholderSuffix: string;
  load: () => Promise<AngleTemplate[]>;
  save: (templates: AngleTemplate[]) => Promise<AngleTemplate[]>;
}

export function AngleTemplatesEditor({
  title,
  description,
  placeholderName,
  placeholderSuffix,
  load,
  save,
}: Props) {
  const [templates, setTemplates] = useState<AngleTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    load()
      .then((list) => setTemplates(list.length > 0 ? list : []))
      .catch((err) => toast.error(err instanceof Error ? err.message : '加载失败'))
      .finally(() => setLoading(false));
    // 切换三类 tab 会切换 load 引用 → 重新加载
  }, [load]);

  const updateRow = (index: number, key: keyof AngleTemplate, value: string) => {
    setTemplates((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [key]: value };
      return next;
    });
  };

  const addRow = () => {
    setTemplates((prev) => [...prev, { name: '', promptSuffix: '' }]);
  };

  const removeRow = (index: number) => {
    setTemplates((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    const filtered = templates.filter((t) => t.name.trim() && t.promptSuffix.trim());
    if (filtered.length === 0) {
      toast.error('至少需要 1 条模板');
      return;
    }
    setSaving(true);
    try {
      const updated = await save(filtered);
      setTemplates(updated);
      toast.success('已保存');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-light-muted">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        加载中
      </div>
    );
  }

  return (
    <div className="card p-6 max-w-3xl space-y-4">
      <div>
        <h3 className="text-lg font-semibold mb-1">{title}</h3>
        <p className="text-sm text-light-muted">{description}</p>
      </div>

      <div className="space-y-2">
        {templates.length === 0 && (
          <p className="text-sm text-light-muted py-4">暂无模板，点击下方"添加模板"</p>
        )}
        {templates.map((tpl, idx) => (
          <div key={idx} className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
            <div className="flex-1 grid sm:grid-cols-[160px,1fr] gap-2">
              <input
                value={tpl.name}
                onChange={(e) => updateRow(idx, 'name', e.target.value)}
                placeholder={placeholderName}
                className="input text-sm"
              />
              <input
                value={tpl.promptSuffix}
                onChange={(e) => updateRow(idx, 'promptSuffix', e.target.value)}
                placeholder={placeholderSuffix}
                className="input text-sm"
              />
            </div>
            <button
              onClick={() => removeRow(idx)}
              className="p-2 text-red-500 hover:bg-red-50 rounded"
              title="删除"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <button onClick={addRow} className="btn-secondary px-3 py-1.5 text-sm flex items-center gap-1">
          <Plus className="w-4 h-4" /> 添加模板
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary px-4 py-1.5 text-sm flex items-center gap-1"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          保存
        </button>
      </div>
    </div>
  );
}
